import { ReactNode, useEffect, useRef } from 'react'
import LoadingIndicator from 'shared/components/loader/LoadingIndicator'
import useIntersectionObserver from 'hooks/useIntersectionObserver'
import cn from 'classnames'

type InfinityViewProps<T> = {
  items: Array<T>
  isReachedEnd: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
  renderItem: (item: T, index: number) => JSX.Element
  emptyView?: ReactNode
  gridClassName: string // to style the grid
  hideSeeMore?: boolean
  hideReachedEnd?: boolean
  children?: ReactNode // to prepend some items on the top if needed without changing this component too much
  appendItem?: ReactNode // to append some items on the bottom if needed without changing this component too much
}

const InfinityView = <T,>(props: InfinityViewProps<T>) => {
  const {
    items,
    isReachedEnd, //
    onLoadMore,
    isLoadingMore,
    renderItem,
    emptyView,
    gridClassName,
    hideSeeMore,
    hideReachedEnd,
    children,
    appendItem,
  } = props

  const indicatorRef = useRef(null)
  const isEmpty = items.length === 0 && isReachedEnd
  const shouldShowLoadingIndicator = !isReachedEnd
  const shouldShowReachedEnd = !isLoadingMore && isReachedEnd && items.length > 0 && !hideReachedEnd
  const shouldShowLoadMore = !isReachedEnd && !isLoadingMore && !isEmpty
  // use intersection observer to detect when the bottom is reached
  const bottomReachedEntry = useIntersectionObserver(indicatorRef, {})
  const isIndicatorInViewport = bottomReachedEntry?.isIntersecting

  useEffect(() => {
    if (!shouldShowLoadMore || !isIndicatorInViewport) return
    onLoadMore()
  }, [shouldShowLoadMore, isIndicatorInViewport, onLoadMore])

  return (
    <div className={cn('w-full  @container/infinity-view', isEmpty ? 'space-y-0' : 'space-y-8')}>
      {/* empty state */}
      {isEmpty && emptyView && (
        <div className="relative inline-flex w-full items-center justify-center">{emptyView}</div>
      )}

      {/* grid items */}
      <div className={gridClassName}>
        {/* use children if you want to prepend some items on the top */}
        {children}
        {items.map(renderItem)}
        {appendItem}
        <div className={cn('invisible w-full', isLoadingMore && 'fixed top-[99999px]')} ref={indicatorRef} />
      </div>

      {/* loading more indicator */}
      {shouldShowLoadingIndicator && (
        <div className="pb-8">
          <LoadingIndicator text="Loading..." />
        </div>
      )}

      {/* ending list */}
      {shouldShowReachedEnd && (
        <div className="inline-flex w-full items-center justify-center p-6 text-xs">
          <span>You've reached the end</span>
        </div>
      )}

      {/* idealy this button will not be shown */}
      <div className={cn('inline-flex w-full items-center justify-center pb-8', { hidden: hideSeeMore })}>
        {/* if somehow the intersection observer fails to trigger load more */}
        {shouldShowLoadMore && (
          <button onClick={onLoadMore} className="text-xs text-primary">
            see more
          </button>
        )}
      </div>
    </div>
  )
}
export default InfinityView
